import React, { useState } from 'react';
import {
  View, Text, StyleSheet, Pressable, ScrollView,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import FontFamily from '../../../../../constants/fonts';

interface MultiSelectDropdownProps {
  label: string;
  required?: boolean;
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  selectAllLabel?: string;
  clearAllLabel?: string;
}

const ChevronDown = ({ open }: { open: boolean }) => (
  <Svg width={16} height={16} viewBox="0 0 24 24" fill="none">
    <Path
      d={open ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'}
      stroke="#616161"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const CheckMark = () => (
  <Svg width={10} height={10} viewBox="0 0 24 24" fill="none">
    <Path
      d="m5 12.5 4.5 4.5L19 7"
      stroke="#fff"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  required,
  options,
  selected,
  onChange,
  placeholder = 'Select…',
  disabled,
  selectAllLabel = 'Select All',
  clearAllLabel = 'Clear All',
}) => {
  const [open, setOpen] = useState(false);

  const toggle = (val: string) => {
    if (selected.includes(val)) {
      onChange(selected.filter(s => s !== val));
    } else {
      onChange([...selected, val]);
    }
  };

  return (
    <View style={s.wrapper}>
      {/* Label */}
      <View style={s.labelRow}>
        <Text style={s.label}>{label}</Text>
        {required && <Text style={s.req}> *</Text>}
      </View>

      {/* Trigger */}
      <Pressable
        style={[s.trigger, open && s.triggerOpen, disabled && s.triggerDisabled]}
        onPress={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
      >
        <View style={s.triggerInner}>
          {selected.length === 0 ? (
            <Text style={s.placeholder}>{placeholder}</Text>
          ) : (
            <View style={s.tagWrap}>
              {selected.map(v => (
                <View key={v} style={s.tag}>
                  <Text style={s.tagText}>{v}</Text>
                </View>
              ))}
            </View>
          )}
        </View>
        <ChevronDown open={open} />
      </Pressable>

      {/* Inline dropdown */}
      {open && (
        <View style={s.dropdown}>
          {/* Select All / Clear All */}
          <View style={s.actions}>
            <Pressable onPress={() => onChange([...options])}>
              <Text style={s.actText}>{selectAllLabel}</Text>
            </Pressable>
            <View style={s.actionsDivider} />
            <Pressable onPress={() => onChange([])}>
              <Text style={s.actText}>{clearAllLabel}</Text>
            </Pressable>
          </View>
          {/* Options list */}
          <ScrollView style={s.list} showsVerticalScrollIndicator={false} nestedScrollEnabled>
            {options.map(opt => {
              const checked = selected.includes(opt);
              return (
                <Pressable
                  key={opt}
                  style={s.item}
                  onPress={() => toggle(opt)}
                >
                  <View style={[s.checkbox, checked && s.checkboxOn]}>
                    {checked && <CheckMark />}
                  </View>
                  <Text style={s.itemText}>{opt}</Text>
                </Pressable>
              );
            })}
          </ScrollView>
        </View>
      )}
    </View>
  );
};

const s = StyleSheet.create({
  wrapper: { gap: 6 },
  labelRow: { flexDirection: 'row', alignItems: 'center' },
  label: { fontSize: 14, fontWeight: '700', color: '#2D2D2D', fontFamily: FontFamily.bold },
  req: { fontSize: 14, color: '#C12C01', fontFamily: FontFamily.bold },
  trigger: {
    minHeight: 40,
    borderWidth: 1,
    borderColor: '#D0D5DD',
    borderRadius: 6,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: '#fff',
    gap: 8,
  },
  triggerOpen: { borderColor: '#005CBC' },
  triggerDisabled: { backgroundColor: '#F9FAFB', opacity: 0.65 },
  triggerInner: { flex: 1 },
  placeholder: { fontSize: 14, color: '#B1B1B1', fontFamily: FontFamily.regular },
  tagWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 4 },
  tag: { backgroundColor: '#DAEEFA', borderRadius: 12, paddingHorizontal: 7, paddingVertical: 2 },
  tagText: { fontSize: 12, fontWeight: '500', color: '#01346F', fontFamily: FontFamily.medium },
  dropdown: {
    borderWidth: 1,
    borderColor: '#E0E0E0',
    borderRadius: 6,
    backgroundColor: '#fff',
    overflow: 'hidden',
    marginTop: 2,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
    gap: 4,
  },
  actionsDivider: { width: 1, height: 14, backgroundColor: '#E0E0E0', marginHorizontal: 4 },
  actText: { fontSize: 12, fontWeight: '700', color: '#005CBC', fontFamily: FontFamily.bold },
  list: { maxHeight: 168 },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 12,
    paddingVertical: 9,
  },
  checkbox: {
    width: 16,
    height: 16,
    borderWidth: 1.5,
    borderColor: '#9E9E9E',
    borderRadius: 3,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxOn: { backgroundColor: '#005CBC', borderColor: '#005CBC' },
  itemText: { fontSize: 14, color: '#2D2D2D', fontFamily: FontFamily.regular },
});

export default MultiSelectDropdown;
